import { useState } from 'react'
import { Check, Copy, ExternalLink, Github } from 'lucide-react'
import { Button } from '@/components/ui'
import type { DeviceFlowState } from '@/hooks/useGitHubDeviceFlow'

export function CopilotDeviceFlowPanel({ state, onInitiate, onReset }: {
  state: DeviceFlowState
  onInitiate: () => void
  onReset: () => void
}) {
  const [copied, setCopied] = useState(false)

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (state.phase === 'idle') {
    return (
      <div className="flex flex-col items-center gap-3 text-center" data-testid="device-flow-idle">
        <p className="text-sm text-muted-foreground">
          Sign in with GitHub to connect Copilot for this project.
        </p>
        <Button type="button" onClick={onInitiate} data-testid="device-flow-connect-button">
          <Github className="h-4 w-4" />
          Sign in with GitHub
        </Button>
      </div>
    )
  }

  if (state.phase === 'initiating') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="device-flow-initiating">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        Starting GitHub authentication...
      </div>
    )
  }

  if (state.phase === 'awaiting_user') {
    return (
      <div className="space-y-4 rounded-md border p-4" data-testid="device-flow-awaiting">
        <div className="space-y-2">
          <p className="text-sm font-medium">Enter this code on GitHub:</p>
          <div className="flex items-center gap-2">
            <code className="rounded bg-muted px-3 py-2 text-2xl font-mono font-bold tracking-widest" data-testid="device-flow-user-code">
              {state.userCode}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => copyCode(state.userCode)}
              aria-label="Copy code"
              data-testid="device-flow-copy-button"
            >
              {copied ? <Check className="h-4 w-4 text-success-foreground" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={state.verificationUri}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary underline hover:text-primary/80"
            data-testid="device-flow-verification-link"
          >
            Open GitHub <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          Waiting for authorization...
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onReset} data-testid="device-flow-cancel-button">
          Cancel
        </Button>
      </div>
    )
  }

  if (state.phase === 'complete') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-success-foreground bg-success p-3" data-testid="device-flow-complete">
        <Check className="h-5 w-5 text-success-foreground" />
        <span className="text-sm font-medium text-success-foreground">
          Successfully connected with GitHub!
        </span>
      </div>
    )
  }

  if (state.phase === 'expired') {
    return (
      <div className="space-y-2" data-testid="device-flow-expired">
        <p className="text-sm text-destructive" role="alert">The authentication code has expired.</p>
        <Button type="button" variant="outline" onClick={onInitiate} data-testid="device-flow-retry-button">
          Try again
        </Button>
      </div>
    )
  }

  if (state.phase === 'access_denied') {
    return (
      <div className="space-y-2" data-testid="device-flow-denied">
        <p className="text-sm text-destructive" role="alert">Access was denied. You may have rejected the authorization request.</p>
        <Button type="button" variant="outline" onClick={onInitiate} data-testid="device-flow-retry-button">
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2" data-testid="device-flow-error">
      <p className="text-sm text-destructive" role="alert">{state.message}</p>
      <Button type="button" variant="outline" onClick={onReset} data-testid="device-flow-retry-button">
        Try again
      </Button>
    </div>
  )
}
